import type { Tokens } from "@spf/api-client";

const STORAGE_KEY = "spf.session";

/**
 * Before refresh tokens existed the access token was stored on its own. Anyone
 * signed in across that change has a value here that can never be renewed, so
 * it is discarded rather than left to expire into a confusing half-session.
 */
const LEGACY_KEY = "spf.accessToken";

try {
  localStorage.removeItem(LEGACY_KEY);
} catch {
  // Storage is unavailable, so there is nothing left over to remove.
}

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * The session can end from outside React, when the API client finds the
 * refresh token refused mid-request. Without a subscription the app keeps
 * rendering an authenticated view whose every call now fails, and the user is
 * shown a connectivity error with a retry that cannot succeed.
 */
export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Tokens live in localStorage rather than a cookie so the same mechanism works
 * unchanged for a native client, per docs/REQUIREMENTS.md section 3. Storage
 * throws in some private modes, so every access is guarded.
 */
export function readTokens(): Tokens | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  // Anything can be in storage: another tab's older format, a truncated write,
  // a person experimenting in the console. Unusable means signed out.
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { accessToken, refreshToken } = parsed as Record<string, unknown>;
    if (typeof accessToken !== "string" || typeof refreshToken !== "string") return null;
    return { accessToken, refreshToken };
  } catch {
    return null;
  }
}

export function writeTokens(tokens: Tokens): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // A session that cannot be persisted still works until reload.
  }
  notify();
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Nothing to clear.
  }
  notify();
}

export function isSignedIn(): boolean {
  return readTokens() !== null;
}
